import { useMemo, useState } from "react";
import { previewDesignStage, useContractorPreview } from "@/features/orders/contractorPreview";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Download,
  FileText,
  Flag,
  Lock,
  PackageCheck,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  Undo2,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useResources } from "@/features/repository/hooks/useRepository";
import {
  useProjectEvents,
  useTechnicalProject,
  useTechnicalProjectByOrder,
  useTechnicalProjectMutations,
} from "../hooks";
import {
  DELIVERABLE_EXECUTOR_STATES,
  DELIVERABLE_STATE_COLOR,
  DELIVERABLE_STATE_LABEL,
  type DeliverableState,
  type DeliverableTarget,
  type PhaseTimelineEntry,
  type PowerProject,
  type TechnicalDeliverable,
  type TechnicalProject,
  type TechPhaseAState,
  type TemplateResourceRef,
} from "../types";
import { fmtDateTime } from "../utils";
import { WorkUnitPanel } from "./WorkUnitPanel";
import { DesignOrderForm, OrderLinksPreview } from "./DesignOrderForm";
import { StageProceedingPanel } from "./StageProceedingPanel";
import { ProjectEventList } from "./ProgressTimelineTab";

// Stage-level event log (kto / kiedy) — read from the unified ProjectEvent feed, sliced to the
// design-stage kinds. Reuses the Historia postępów renderer.
export function StageHistory({
  connId,
  kinds,
}: {
  connId: string;
  kinds: string;
}) {
  const { data: events = [] } = useProjectEvents(connId, { kind: kinds });
  return (
    <section className="space-y-2">
      <h3 className="section-label">Historia zdarzeń (kto / kiedy)</h3>
      <ProjectEventList events={events} />
    </section>
  );
}

const PHASE_A_STEPS: { key: string; label: string }[] = [
  { key: "not_started", label: "Start" },
  { key: "concept_sent", label: "Koncepcja → ZE" },
  { key: "concept_accepted", label: "Akceptacja koncepcji" },
  { key: "route_finalized", label: "Finalna akceptacja trasy" },
];

const stateOptions = DELIVERABLE_EXECUTOR_STATES.map((v) => ({
  value: v,
  label: DELIVERABLE_STATE_LABEL[v],
}));

// Note that gets stamped on every deliverable card (and the section banner) while the route
// acceptance is withdrawn — so it is unambiguous that work is paused, not deleted.
const SUSPENDED_DELIVERABLE_NOTE =
  "Wstrzymano — wycofano finalną akceptację trasy. Edycja zablokowana do ponownej akceptacji.";

function PhaseStepper({ current }: { current: TechPhaseAState }) {
  const idx = PHASE_A_STEPS.findIndex((s) => s.key === current);
  // The terminal state is an accepted/done state, not "in progress" — render the whole chain as
  // completed (green + ✓) so it never reads like an alert. Intermediate "current" steps stay amber.
  const finalized = current === "route_finalized";
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {PHASE_A_STEPS.map((s, i) => {
        const done = finalized || i < idx;
        const active = !finalized && i === idx;
        return (
          <div key={s.key} className="flex items-center gap-1">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${
                done
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : active
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-400 dark:bg-amber-900/30 dark:text-amber-300"
                    : "bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]"
              }`}
            >
              {done && <Check size={11} />}
              {s.label}
            </span>
            {i < PHASE_A_STEPS.length - 1 && (
              <span className="text-[var(--color-text-muted)]">›</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Plain-language status line so it is obvious at a glance whether the route is accepted — the
// stepper alone was ambiguous (the final step looked like an alert). Suspension takes priority.
function RouteStatusBanner({ tp }: { tp: TechnicalProject }) {
  if (tp.is_suspended) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold">
            Wstrzymano — wycofano finalną akceptację trasy.
          </div>
          <div className="text-xs">
            Opracowania pozostają widoczne, ale są zablokowane do ponownej
            finalnej akceptacji trasy.
            {tp.route_revert_reason ? ` Powód: ${tp.route_revert_reason}` : ""}
          </div>
        </div>
      </div>
    );
  }
  if (tp.phase_a_state === "route_finalized") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-green-800 dark:border-green-700/60 dark:bg-green-900/20 dark:text-green-300">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold">Trasa zaakceptowana.</div>
          <div className="text-xs">
            Finalna akceptacja trasy
            {tp.route_finalized_at
              ? ` — ${fmtDateTime(tp.route_finalized_at)}`
              : ""}
            . Opracowania są odblokowane.
          </div>
        </div>
      </div>
    );
  }
  const text =
    tp.phase_a_state === "concept_accepted"
      ? "Koncepcja zaakceptowana przez ZE — oczekuje na finalną akceptację trasy przez GK."
      : tp.phase_a_state === "concept_sent"
        ? "Koncepcja wysłana do ZE — oczekuje na akceptację ZE."
        : "Trasa nie została jeszcze wysłana do ZE.";
  return (
    <div className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-muted)] px-3 py-2 text-sm text-[var(--color-text-muted)]">
      <Flag size={15} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// Friendly per-transition label for the route-approval history. Falls back to the event's own
// title (sans the "Projekt techniczny — " prefix) for any state not listed.
const PHASE_EVENT_LABEL: Partial<Record<TechPhaseAState, string>> = {
  not_started: "Cofnięto do startu (zmiana koncepcji)",
  concept_sent: "Koncepcja wysłana do ZE",
  concept_accepted: "Koncepcja zaakceptowana przez ZE",
  route_finalized: "Finalna akceptacja trasy",
};

// Compact "kto / kiedy" log of the Faza A transitions — rendered inline under the stepper so the
// route's path is auditable on the order page too (where the global event feed is out of reach).
function PhaseTimeline({ entries }: { entries: PhaseTimelineEntry[] }) {
  if (!entries.length) return null;
  return (
    <div className="pt-2 border-t border-[var(--color-border-muted)]">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-2 mt-2">
        Historia trasy (kto / kiedy)
      </div>
      <ol className="relative ml-1.5 border-l border-[var(--color-border)] space-y-2.5">
        {entries.map((e, i) => {
          const label =
            (e.to_state && PHASE_EVENT_LABEL[e.to_state]) ??
            e.title.replace(/^Projekt techniczny — /, "");
          return (
            <li key={i} className="relative pl-4">
              <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-[var(--color-primary)]" />
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <span className="text-xs font-medium text-[var(--color-text)]">
                  {label}
                </span>
                <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums">
                  {e.actor_name} · {fmtDateTime(e.occurred_at)}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function btn(variant: "primary" | "ghost" | "warn" = "primary") {
  const base =
    "inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium disabled:opacity-50";
  if (variant === "primary")
    return `${base} bg-[var(--color-primary)] text-white hover:opacity-90`;
  if (variant === "warn")
    return `${base} bg-amber-600 text-white hover:opacity-90`;
  return `${base} border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]`;
}

// Klikalna referencja do elementu repozytorium z szablonami PZT/PAB/PT (deep-link do
// repozytorium + szybkie pobranie najnowszej wersji), podpięta do zlecenia PT.
function TemplateResourceLink({ info }: { info: TemplateResourceRef }) {
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap">
      <span className="text-[var(--color-text-muted)]">Szablony PZT/PAB/PT:</span>
      <Link
        to={`/repository?resource=${info.id}`}
        className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
      >
        <FileText size={12} /> {info.title}
      </Link>
      {info.file?.url && (
        <a
          href={info.file.url}
          download={info.file.original_filename}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          title="Pobierz najnowszą wersję"
        >
          <Download size={12} /> pobierz
        </a>
      )}
    </div>
  );
}

function AssignmentBlock({
  tp,
  connId,
}: {
  tp: TechnicalProject;
  connId: string;
}) {
  const m = useTechnicalProjectMutations(connId);
  const { data: users = [] } = useInternalUsers();
  const { data: resources = [] } = useResources();
  const [employee, setEmployee] = useState("");
  const [changing, setChanging] = useState(false);
  const userOptions = useMemo(
    () =>
      [...users]
        .sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"))
        .map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );
  // Picker szablonu PT pokazuje wyłącznie zasoby z folderu(-ów) wzorów wskazanych przez
  // backend (dopasowanie po nazwie/opisie kategorii) — reszta repozytorium, w tym pliki
  // firmowe z innych folderów, pozostaje ukryta.
  const templateResourceOptions = useMemo(() => {
    const allowed = new Set(tp.pt_template_category_ids ?? []);
    return resources
      .filter((r) => allowed.has(r.category_id))
      .map((r) => ({ value: r.id, label: r.title }));
  }, [resources, tp.pt_template_category_ids]);

  const [showOrder, setShowOrder] = useState(false);

  if (tp.assignee) {
    const assignee = tp.assignee;
    return (
      <div className="space-y-1.5">
        {assignee.kind === "employee" ? (
          <div className="space-y-1.5">
            <p className="text-xs text-[var(--color-text-muted)]">
              Realizuje pracownik GK:{" "}
              <span className="font-semibold text-[var(--color-text)]">
                {assignee.name}
              </span>
            </p>
            {/* Koordynator projektu / dyrektor może zamienić pracownika GK na innego
                (dopóki etap nie jest zlecony podwykonawcy — wtedy assignee.kind = contractor). */}
            {tp.can_manage &&
              (changing ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-72">
                    <SearchableSelect
                      options={userOptions}
                      value={employee}
                      placeholder="— pracownik GK —"
                      onChange={(e) => setEmployee(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={
                      !employee ||
                      employee === assignee.id ||
                      m.assignEmployee.isPending
                    }
                    onClick={() =>
                      m.assignEmployee.mutate(
                        { id: tp.id, employee },
                        { onSuccess: () => setChanging(false) },
                      )
                    }
                    className={btn()}
                  >
                    Zapisz
                  </button>
                  <button
                    type="button"
                    onClick={() => setChanging(false)}
                    className={btn("ghost")}
                  >
                    Anuluj
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEmployee(assignee.id ?? "");
                    setChanging(true);
                  }}
                  className={btn("ghost")}
                >
                  Zmień pracownika
                </button>
              ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-text-muted)]">
            Realizuje podwykonawca:{" "}
            <span className="font-semibold text-[var(--color-text)]">
              {assignee.name ?? "—"}
            </span>
          </p>
        )}
        <OrderLinksPreview links={tp.order_links} />
        <StageProceedingPanel
          proceeding={tp.proceeding}
          canManage={tp.can_manage}
          stage="technical"
          starting={m.startRfqProceeding.isPending}
          releasing={m.releaseRfqProceeding.isPending}
          onStart={(payload) => m.startRfqProceeding.mutate({ id: tp.id, payload })}
          onRelease={() => m.releaseRfqProceeding.mutate(tp.id)}
          onOrderWinner={() => setShowOrder(true)}
          orderFormOpen={showOrder}
        />
        {/* Etap bywa przypisany pracownikowi GK, a mimo to wyłaniany w przetargu — wtedy
            „Zleć zwycięzcy" musi mieć gdzie otworzyć formularz (zlecenie zdejmie pracownika). */}
        {showOrder && ptWinnerProps(tp) && (
          <DesignOrderForm
            winner={ptWinnerProps(tp)}
            pending={m.createRequisition.isPending}
            design
            connectionId={connId}
            showProjectFileShare
            onSubmit={(fd) =>
              m.createRequisition.mutate({ id: tp.id, fd }, { onSuccess: () => setShowOrder(false) })
            }
            onCancel={() => setShowOrder(false)}
          />
        )}
        {tp.template_resource_info && <TemplateResourceLink info={tp.template_resource_info} />}
      </div>
    );
  }
  // unassigned
  if (!tp.can_manage) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Wykonawca nie został jeszcze przypisany.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {/* Wcześniejsze zamówienia (np. odrzucone/anulowane „główne" lub korekcyjne) zostają
          widoczne jako historia — etap jest znów wolny do zlecenia, ale nie gubimy kontekstu,
          że jakieś zlecenie już było. */}
      {tp.order_links.length > 0 && <OrderLinksPreview links={tp.order_links} />}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-72">
          <SearchableSelect
            options={userOptions}
            value={employee}
            placeholder="— pracownik GK —"
            onChange={(e) => setEmployee(e.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={!employee || m.assignEmployee.isPending}
          onClick={() => m.assignEmployee.mutate({ id: tp.id, employee })}
          className={btn()}
        >
          Przypisz pracownika
        </button>
        {!showOrder && !tp.proceeding?.blocks_direct_order && (
          <button
            type="button"
            onClick={() => setShowOrder(true)}
            className={btn("ghost")}
          >
            …albo zleć podwykonawcy
          </button>
        )}
      </div>
      <StageProceedingPanel
        proceeding={tp.proceeding}
        canManage={tp.can_manage}
        stage="technical"
        starting={m.startRfqProceeding.isPending}
        releasing={m.releaseRfqProceeding.isPending}
        onStart={(payload) => m.startRfqProceeding.mutate({ id: tp.id, payload })}
        onRelease={() => m.releaseRfqProceeding.mutate(tp.id)}
        onOrderWinner={() => setShowOrder(true)}
        orderFormOpen={showOrder}
      />
      {showOrder && (
        <DesignOrderForm
          winner={ptWinnerProps(tp)}
          pending={m.createRequisition.isPending}
          design
          connectionId={connId}
          templateResources={templateResourceOptions}
          defaultTemplateResourceId={tp.default_template_resource?.id}
          showTenderDocs
          showRouteDxf
          showProjectFileShare
          onSubmit={(fd) =>
            m.createRequisition.mutate(
              { id: tp.id, fd },
              { onSuccess: () => setShowOrder(false) },
            )
          }
          onCancel={() => setShowOrder(false)}
        />
      )}
    </div>
  );
}

/** Dane zwycięzcy dla formularza zlecenia — tylko gdy postępowanie czeka na zlecenie. */
function ptWinnerProps(tp: TechnicalProject) {
  const p = tp.proceeding;
  if (!p?.awaiting_order || !p.winner) return undefined;
  return {
    proceedingId: p.id,
    number: p.number,
    title: p.title,
    contractorName: p.winner.contractor_name,
    amountNet: p.winner.amount_net,
  };
}

function DeliverableCard({
  d,
  tp,
  connId,
  lockedNote,
}: {
  d: TechnicalDeliverable;
  tp: TechnicalProject;
  connId: string;
  lockedNote?: string;
}) {
  const m = useTechnicalProjectMutations(connId);
  const busy =
    m.setState.isPending ||
    m.accept.isPending ||
    m.requestCorrection.isPending ||
    m.addFiles.isPending ||
    m.addComment.isPending ||
    m.deleteDeliverable.isPending;

  const badges = (
    <>
      <span className="px-1.5 py-px rounded text-[10px] font-semibold uppercase bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]">
        {d.target_display}
      </span>
      {d.is_custom && tp.can_manage && (
        <button
          type="button"
          disabled={busy}
          onClick={() => m.deleteDeliverable.mutate(d.id)}
          className="inline-flex items-center gap-0.5 text-[11px] text-red-500 hover:underline disabled:opacity-50"
          title="Usuń opracowanie (tylko dodane ręcznie)"
        >
          <Trash2 size={11} /> usuń
        </button>
      )}
    </>
  );

  return (
    <WorkUnitPanel
      title={d.display_name}
      badges={badges}
      files={d.attachments}
      timeline={d.timeline}
      statusLabels={DELIVERABLE_STATE_LABEL}
      canWrite={tp.can_write}
      canManage={tp.can_manage}
      busy={busy}
      lockedNote={lockedNote}
      state={d.state}
      stateLabel={
        DELIVERABLE_STATE_LABEL[d.state as DeliverableState] ?? d.state_display
      }
      stateColor={
        DELIVERABLE_STATE_COLOR[d.state as DeliverableState] ?? "#6b7280"
      }
      stateOptions={stateOptions}
      onSetState={(state) => m.setState.mutate({ id: d.id, state })}
      onAccept={() => m.accept.mutate({ id: d.id })}
      acceptDisabled={d.state === "accepted"}
      onRequestCorrection={(note) =>
        m.requestCorrection.mutate({ id: d.id, note })
      }
      onAddFiles={(files) => m.addFiles.mutate({ id: d.id, files })}
      onComment={(body) => m.addComment.mutate({ id: d.id, body })}
    />
  );
}

function AddDeliverable({
  tp,
  connId,
}: {
  tp: TechnicalProject;
  connId: string;
}) {
  const m = useTechnicalProjectMutations(connId);
  const [label, setLabel] = useState("");
  const [target, setTarget] = useState<DeliverableTarget>("ze");
  if (!tp.can_manage) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input
        className="input-base text-xs flex-1 min-w-[12rem]"
        placeholder="Nowe opracowanie (np. Projekt zagospodarowania)…"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <select
        className="input-base text-xs w-auto"
        value={target}
        onChange={(e) => setTarget(e.target.value as DeliverableTarget)}
      >
        <option value="ze">ZE</option>
        <option value="urzad">Urząd</option>
        <option value="urzad_ze">Urząd + ZE</option>
      </select>
      <button
        type="button"
        disabled={!label.trim() || m.addDeliverable.isPending}
        onClick={() =>
          m.addDeliverable.mutate(
            { id: tp.id, label: label.trim(), target },
            { onSuccess: () => setLabel("") },
          )
        }
        className={btn()}
      >
        <Plus size={13} /> Dodaj opracowanie
      </button>
    </div>
  );
}

function BundleRow({
  tp,
  connId,
  target,
  label,
  withReceive,
}: {
  tp: TechnicalProject;
  connId: string;
  target: "ze" | "urzad" | "tom_prawny"; // bundle recipients only (never the deliverable's "urzad_ze")
  label: string;
  withReceive?: boolean;
}) {
  const m = useTechnicalProjectMutations(connId);
  const state =
    target === "ze" ? tp.ze_state : target === "urzad" ? tp.urzad_state : tp.tom_prawny_state;
  const stateDisplay =
    target === "ze" ? tp.ze_state_display : target === "urzad" ? tp.urzad_state_display : tp.tom_prawny_state_display;
  const busy =
    m.submitBundle.isPending ||
    m.acceptBundle.isPending ||
    m.rejectBundle.isPending ||
    m.receiveZe.isPending;
  const rejected = state === "rejected";
  return (
    <div className="flex items-center gap-3 py-2 flex-wrap">
      <span className="text-sm font-semibold text-[var(--color-text)] w-40">
        {label}
      </span>
      <span
        className={`text-xs ${rejected ? "text-red-500 font-medium" : "text-[var(--color-text-muted)]"}`}
      >
        {stateDisplay}
      </span>
      {/* Faza C/D is OURS — the subcontractor sees these statuses read-only (no buttons). */}
      <div className="flex items-center gap-2 ml-auto">
        {(state === "not_sent" || rejected) && tp.can_manage && (
          <button
            type="button"
            disabled={busy}
            onClick={() => m.submitBundle.mutate({ id: tp.id, target })}
            className={btn("ghost")}
          >
            <Send size={13} /> {rejected ? "Wyślij ponownie" : "Wyślij"}
          </button>
        )}
        {state === "sent" && tp.can_manage && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => m.acceptBundle.mutate({ id: tp.id, target })}
              className={btn()}
            >
              <CheckCircle2 size={13} /> Akceptacja
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => m.rejectBundle.mutate({ id: tp.id, target })}
              className={btn("warn")}
            >
              <AlertTriangle size={13} /> Odrzuć — do poprawy
            </button>
          </>
        )}
        {withReceive &&
          state === "accepted" &&
          !tp.ze_received_at &&
          tp.can_manage && (
            <button
              type="button"
              disabled={busy}
              onClick={() => m.receiveZe.mutate(tp.id)}
              className={btn()}
            >
              <PackageCheck size={13} /> Odbiór przez ZE
            </button>
          )}
        {withReceive && tp.ze_received_at && (
          <span className="text-xs text-green-600 font-medium">
            Odebrano przez ZE
          </span>
        )}
      </div>
    </div>
  );
}

// GK-only control to withdraw the final route acceptance. Opens an inline reason box; the stage
// drops back to "Akceptacja koncepcji" and Faza B freezes (without losing any deliverable).
function RevertRouteControl({
  tp,
  connId,
}: {
  tp: TechnicalProject;
  connId: string;
}) {
  const m = useTechnicalProjectMutations(connId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!tp.can_manage || tp.phase_a_state !== "route_finalized") return null;
  if (!open) {
    return (
      <button
        type="button"
        disabled={m.revertRoute.isPending}
        onClick={() => setOpen(true)}
        className={btn("warn")}
      >
        <Undo2 size={13} /> Cofnij finalną akceptację trasy
      </button>
    );
  }
  return (
    <div className="basis-full rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2 dark:border-amber-700/60 dark:bg-amber-900/20">
      <p className="text-xs text-amber-800 dark:text-amber-300">
        Cofnięcie wróci etap do „Akceptacja koncepcji". Opracowania nie znikną —
        zostaną zablokowane do ponownej finalnej akceptacji trasy.
      </p>
      <textarea
        className="input-base text-xs w-full min-h-[2.25rem]"
        rows={2}
        placeholder="Powód cofnięcia (opcjonalnie, pokażemy go przy opracowaniach)…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={m.revertRoute.isPending}
          onClick={() =>
            m.revertRoute.mutate(
              { id: tp.id, reason: reason.trim() },
              {
                onSuccess: () => {
                  setOpen(false);
                  setReason("");
                },
              },
            )
          }
          className={btn("warn")}
        >
          <Undo2 size={13} /> Cofnij akceptację
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className={btn("ghost")}
        >
          Anuluj
        </button>
      </div>
    </div>
  );
}

export function TechnicalProjectTab({
  connection,
  orderId,
}: {
  connection?: PowerProject;
  orderId?: string;
}) {
  const byConn = useTechnicalProject(orderId ? undefined : connection?.id);
  const byOrder = useTechnicalProjectByOrder(orderId);
  // „Widok podwykonawcy": flagi z API są dla użytkownika GK — przeliczamy je jak dla wykonawcy.
  const { isPreview } = useContractorPreview();
  const tp = orderId
    ? byOrder.data && isPreview
      ? previewDesignStage(byOrder.data, orderId)
      : byOrder.data
    : byConn.data;
  const isLoading = orderId ? byOrder.isLoading : byConn.isLoading;
  const connId = tp?.connection ?? connection?.id ?? "";
  const m = useTechnicalProjectMutations(connId);
  const [showCorrection, setShowCorrection] = useState(false);

  // Embedded on the subcontractor's Order page (orderId): stay invisible when there's no design
  // stage for this order (non-design order, or not theirs) — analogous to the WPW panel.
  if (orderId && (isLoading || !tp)) return null;
  if (isLoading)
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  if (!tp) {
    return (
      <p className="text-sm text-[var(--color-text-muted)] py-6">
        Brak dostępu do projektu technicznego.
      </p>
    );
  }

  const phaseBusy =
    m.sendConcept.isPending ||
    m.acceptConcept.isPending ||
    m.requestConceptChange.isPending ||
    m.finalizeRoute.isPending;

  return (
    <div className="space-y-5 pt-2 pb-8">
      {orderId && (
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[var(--color-text)]">
            Projekt techniczny — ścieżka operacyjna
          </h2>
          <span className="text-[10px] font-bold px-1.5 py-px rounded bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
            DLC_01
          </span>
        </div>
      )}
      {/* Faza A — trasa / koncepcja */}
      <section className="card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="section-label">Zatwierdzenie trasy</h3>
          <span className="text-[10px] font-bold px-1.5 py-px rounded bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
            DLC_01
          </span>
        </div>
        <PhaseStepper current={tp.phase_a_state} />
        <RouteStatusBanner tp={tp} />
        <div className="flex items-center gap-2 flex-wrap">
          {tp.phase_a_state === "not_started" && tp.can_write && (
            <button
              type="button"
              disabled={phaseBusy}
              onClick={() => m.sendConcept.mutate(tp.id)}
              className={btn()}
            >
              <Send size={13} /> Wyślij koncepcję do ZE
            </button>
          )}
          {tp.phase_a_state === "concept_sent" && tp.can_write && (
            <button
              type="button"
              disabled={phaseBusy}
              onClick={() => m.acceptConcept.mutate(tp.id)}
              className={btn()}
            >
              <CheckCircle2 size={13} /> Koncepcja zaakceptowana przez ZE
            </button>
          )}
          {(tp.phase_a_state === "concept_sent" ||
            tp.phase_a_state === "concept_accepted") &&
            tp.can_write && (
              <button
                type="button"
                disabled={phaseBusy}
                onClick={() => m.requestConceptChange.mutate(tp.id)}
                className={btn("ghost")}
              >
                <RotateCcw size={13} /> Zgłoś zmianę koncepcji (do startu)
              </button>
            )}
          {tp.phase_a_state === "concept_accepted" && tp.can_manage && (
            <button
              type="button"
              disabled={phaseBusy}
              onClick={() => m.finalizeRoute.mutate({ id: tp.id })}
              className={btn()}
            >
              <Flag size={13} /> Finalna akceptacja trasy
            </button>
          )}
          {tp.phase_a_state === "concept_accepted" && !tp.can_manage && (
            <span className="text-xs text-[var(--color-text-muted)]">
              Finalną akceptację trasy ustawia kierownik przedsięwzięcia /
              dyrektor.
            </span>
          )}
          <RevertRouteControl tp={tp} connId={connId} />
        </div>
        <PhaseTimeline entries={tp.phase_a_timeline} />
        {!orderId && (
          <div className="pt-1 border-t border-[var(--color-border-muted)]">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-1 mt-2">
              Wykonawca
            </div>
            <AssignmentBlock tp={tp} connId={connId} />
          </div>
        )}
      </section>

      {/* Faza B — opracowania */}
      <section className="space-y-2">
        <h3 className="section-label">
          Opracowania (PT / PB + dodatkowe)
        </h3>
        {tp.phase_a_state === "route_finalized" ? (
          <div className="space-y-3">
            {tp.deliverables.map((d) => (
              <DeliverableCard key={d.key} d={d} tp={tp} connId={connId} />
            ))}
            <AddDeliverable tp={tp} connId={connId} />
          </div>
        ) : tp.is_suspended ? (
          // Reverted route: keep the deliverables visible but frozen, with the reason spelled out
          // on the section banner and on every card — so nothing looks lost or accidentally editable.
          <div className="space-y-3">
            <div className="card p-3 flex items-start gap-2 text-sm border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300">
              <Lock size={15} className="mt-0.5 shrink-0" />
              <span>
                Opracowania są wstrzymane — wycofano finalną akceptację trasy.
                Pozostają widoczne, ale zablokowane do ponownej akceptacji
                trasy.
                {tp.route_revert_reason
                  ? ` Powód: ${tp.route_revert_reason}`
                  : ""}
              </span>
            </div>
            {tp.deliverables.map((d) => (
              <DeliverableCard
                key={d.key}
                d={d}
                tp={tp}
                connId={connId}
                lockedNote={SUSPENDED_DELIVERABLE_NOTE}
              />
            ))}
          </div>
        ) : (
          <div className="card p-4 flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <Lock size={15} /> Opracowania odblokują się po finalnej akceptacji
            trasy.
          </div>
        )}
      </section>

      {/* Faza C/D — złożenie, odbiory */}
      <section className="card p-4 space-y-1">
        <h3 className="section-label mb-2">Złożenie i odbiory</h3>
        <div className="divide-y divide-[var(--color-border-muted)]">
          <BundleRow
            tp={tp}
            connId={connId}
            target="urzad"
            label="Projekt budowlany → Urząd"
          />
          <BundleRow
            tp={tp}
            connId={connId}
            target="ze"
            label="PT + … → ZE"
            withReceive
          />
          <BundleRow
            tp={tp}
            connId={connId}
            target="tom_prawny"
            label="Tom prawny"
          />
        </div>
        {/* Corrections are a management concern (PM/director). Settlement is intentionally
            not surfaced here yet — no link to rozliczenie in this view. */}
        {tp.can_manage && (
          <>
            <div className="flex items-center gap-3 pt-3 mt-1 border-t border-[var(--color-border-muted)] flex-wrap">
              {!showCorrection && (
                <button
                  type="button"
                  onClick={() => setShowCorrection(true)}
                  className={btn("ghost")}
                >
                  <AlertTriangle size={13} /> Zamówienie korekcyjne (poprawki)
                </button>
              )}
            </div>
            {showCorrection && (
              <DesignOrderForm
                kind="correction"
                pending={m.createRequisition.isPending}
                connectionId={connId}
                showProjectFileShare
                onSubmit={(fd) =>
                  m.createRequisition.mutate(
                    { id: tp.id, fd },
                    { onSuccess: () => setShowCorrection(false) },
                  )
                }
                onCancel={() => setShowCorrection(false)}
              />
            )}
          </>
        )}
      </section>

      {!orderId && (
        <StageHistory
          connId={connId}
          kinds="tech_project_phase_changed,tech_deliverable_changed"
        />
      )}
    </div>
  );
}
